import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputBase,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  validateReportingPeriodV01,
  type ReportingPeriodV01,
} from '../../../hooks/advisory/advisoryReportingPeriod';
import { normalizeControlCharacters } from '../../../redux/slices/advisory/advisoryTextSanitize';
import {
  eyebrow,
  focusRing,
  meta,
  mono,
  pillAction,
  pressable,
} from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { AdvisoryPeriodEditor } from './AdvisoryPeriodEditor';
import { AdvisoryReviewWarning } from './AdvisoryReviewWarning';
import { isTextSelectionClick, rememberSelectionOnPointerDown } from './advisoryTextSelection';

export type AdvisoryReviewField =
  | 'subjectCompany'
  | 'counterparties'
  | 'reportingPeriod'
  | 'counterpartyKind';
export type AdvisoryReviewEditableField = Exclude<AdvisoryReviewField, 'counterpartyKind'>;
export type AdvisoryReviewSegment =
  | { type: 'text'; text: string }
  | { type: 'field'; field: Exclude<AdvisoryReviewField, 'counterpartyKind'> };
export interface AdvisoryUnifiedReviewDraft {
  subjectCompany: string;
  counterparties: string[];
  reportingPeriod: ReportingPeriodV01 | null;
}
export interface AdvisoryReviewConflict {
  field: AdvisoryReviewField;
  mine: string;
  suggestion: string;
  reason?: string;
}
export interface AdvisoryUnifiedReviewScreenProps {
  draft: AdvisoryUnifiedReviewDraft;
  statement: AdvisoryReviewSegment[];
  counterpartyKind?: 'competitor' | 'acquisition target' | null;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  onChange: (draft: AdvisoryUnifiedReviewDraft, field: AdvisoryReviewEditableField) => void;
  onEditStart?: (field: AdvisoryReviewEditableField) => void;
  onEditCancel?: (field: AdvisoryReviewEditableField) => void;
  onRefine: (feedback: string) => void | Promise<unknown>;
  onConfirm: () => void | Promise<unknown>;
  saving?: boolean;
  refining?: boolean;
  preparing?: boolean;
  confirming?: boolean;
  editingDisabled?: boolean;
  confirmationDisabled?: boolean;
  refinementDisabled?: boolean;
  canRefine?: boolean;
  refinementsRemaining?: number | null;
  periodState?: 'found' | 'defaulted' | 'failed' | 'missing';
  errorI18nKey?: string | null;
  conflicts?: AdvisoryReviewConflict[];
  problemFields?: AdvisoryReviewField[];
  onResolveConflict?: (field: AdvisoryReviewField, choice: 'keep' | 'suggestion') => void;
}

const editText = (theme: Theme) => ({
  minWidth: 0,
  padding: 0,
  font: 'inherit',
  lineHeight: 'inherit',
  verticalAlign: 'baseline',
  borderRadius: 0,
  textTransform: 'none',
  userSelect: 'text',
  color: theme.colors.lavenderDeep,
  textDecoration: 'underline dotted',
  textUnderlineOffset: '0.2em',
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline dotted' },
});
const problemText = (theme: Theme) => ({ color: theme.colors.reviewWarning });
const refinementAction = (theme: Theme) => ({
  minWidth: 0,
  padding: 0,
  color: theme.colors.lavenderDeep,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
    textUnderlineOffset: '0.2em',
  },
});
const companyInput = (theme: Theme) => ({
  color: theme.colors.inkPrimary,
  fontSize: theme.visualPrimitives.inputSize,
  borderBottom: '1px solid ' + theme.visualPrimitives.border,
  '& .MuiInputBase-input': { padding: theme.visualPrimitives.inputPaddingY },
  '&:focus-within': { borderBottomColor: theme.colors.lavenderDeep },
});
const companyKey = (value: string) => normalizeControlCharacters(value).toLowerCase();
type CompanyError =
  | 'subjectRequired'
  | 'comparisonRequired'
  | 'blankCompany'
  | 'duplicateCompany'
  | 'subjectExcluded'
  | 'companyTooLong'
  | null;
function subjectError(subject: string, companies: string[]): CompanyError {
  if (!companyKey(subject)) return 'subjectRequired';
  if (normalizeControlCharacters(subject).length > 120) return 'companyTooLong';
  if (companies.some(name => companyKey(name) === companyKey(subject))) return 'subjectExcluded';
  return null;
}
function comparisonError(subject: string, companies: string[], required = false): CompanyError {
  if (required && companies.length === 0) return 'comparisonRequired';
  if (companies.some(name => normalizeControlCharacters(name).length > 120))
    return 'companyTooLong';
  if (companies.some(name => !companyKey(name))) return 'blankCompany';
  if (new Set(companies.map(companyKey)).size !== companies.length) return 'duplicateCompany';
  if (companyKey(subject) && companies.some(name => companyKey(name) === companyKey(subject)))
    return 'subjectExcluded';
  return null;
}
export function reviewCompanyError(
  subject: string,
  companies: string[],
  required = false
): CompanyError {
  return subjectError(subject, companies) ?? comparisonError(subject, companies, required);
}

/** All edits are controlled draft changes. Network orchestration belongs to the caller. */
export function AdvisoryUnifiedReviewScreen({
  draft,
  statement,
  counterpartyKind = null,
  feedback,
  onFeedbackChange,
  onChange,
  onEditStart,
  onEditCancel,
  onRefine,
  onConfirm,
  saving = false,
  refining = false,
  preparing = false,
  confirming = false,
  editingDisabled = false,
  confirmationDisabled = false,
  refinementDisabled = false,
  canRefine = true,
  refinementsRemaining = null,
  periodState = 'found',
  errorI18nKey = null,
  conflicts = [],
  problemFields = [],
  onResolveConflict,
}: AdvisoryUnifiedReviewScreenProps) {
  const { t } = useUntypedTranslation();
  const [companyDialog, setCompanyDialog] = useState<'subject' | 'counterparties'>('subject');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [periodEditing, setPeriodEditing] = useState(false);
  const [refinementOpen, setRefinementOpen] = useState(Boolean(feedback));
  const feedbackInput = useRef<HTMLTextAreaElement | null>(null);
  const refinementTrigger = useRef<HTMLButtonElement | null>(null);
  const statementElement = useRef<HTMLDivElement | null>(null);
  const refinementDismissed = useRef(false);
  const restoreRefinementFocus = useRef(false);
  useEffect(() => {
    if (feedback && !refinementDismissed.current) setRefinementOpen(true);
  }, [feedback]);
  useEffect(() => {
    if (!refinementOpen && restoreRefinementFocus.current) {
      restoreRefinementFocus.current = false;
      const trigger = refinementTrigger.current;
      if (trigger && !trigger.disabled) trigger.focus();
      else statementElement.current?.focus();
    }
  }, [refinementOpen]);
  const [companyDraft, setCompanyDraft] = useState({
    subjectCompany: '',
    counterparties: [] as string[],
  });
  const [focusedRow, setFocusedRow] = useState<'subject' | number>('subject');
  const companyInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const dialogTrigger = useRef<HTMLElement | null>(null);
  const comparisonTrigger = useRef<HTMLButtonElement | null>(null);
  const [localAction, setLocalAction] = useState<'refine' | 'confirm' | null>(null);
  const actionRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const editingBusy =
    editingDisabled || refining || preparing || confirming || localAction !== null;
  const busy = saving || editingBusy;
  const cancelRefinement = () => {
    if (editingBusy || actionRef.current) return;
    refinementDismissed.current = true;
    restoreRefinementFocus.current = true;
    setRefinementOpen(false);
  };
  const companiesError = reviewCompanyError(
    draft.subjectCompany,
    counterpartyKind ? draft.counterparties : [],
    Boolean(counterpartyKind)
  );
  const dialogError =
    companyDialog === 'subject'
      ? subjectError(companyDraft.subjectCompany, counterpartyKind ? draft.counterparties : [])
      : comparisonError(
          draft.subjectCompany,
          companyDraft.counterparties,
          Boolean(counterpartyKind)
        );
  const needsAttention = (field: AdvisoryReviewField) =>
    problemFields.includes(field) || conflicts.some(conflict => conflict.field === field);
  const subjectProblem =
    needsAttention('subjectCompany') ||
    Boolean(subjectError(draft.subjectCompany, counterpartyKind ? draft.counterparties : []));
  const comparisonProblem = needsAttention('counterparties') || needsAttention('counterpartyKind');
  const companyProblem = (name: string) =>
    comparisonProblem ||
    !companyKey(name) ||
    normalizeControlCharacters(name).length > 120 ||
    companyKey(name) === companyKey(draft.subjectCompany) ||
    draft.counterparties.filter(other => companyKey(other) === companyKey(name)).length > 1;
  const validPeriod =
    draft.reportingPeriod !== null && validateReportingPeriodV01(draft.reportingPeriod).ok;
  const periodProblem =
    !validPeriod || periodState !== 'found' || needsAttention('reportingPeriod');
  const warningKeys = Array.from(
    new Set(
      [
        subjectError(draft.subjectCompany, counterpartyKind ? draft.counterparties : []),
        counterpartyKind ? comparisonError(draft.subjectCompany, draft.counterparties, true) : null,
        !validPeriod ? 'periodRequired' : null,
      ].filter((key): key is string => key !== null)
    )
  );
  const confirmBlocked =
    busy ||
    confirmationDisabled ||
    conflicts.length > 0 ||
    Boolean(companiesError) ||
    !validPeriod ||
    dialogOpen ||
    periodEditing;
  const refineBlocked =
    refinementDisabled ||
    busy ||
    !canRefine ||
    !feedback.trim() ||
    feedback.length > 8000 ||
    dialogOpen ||
    periodEditing;
  const kindKey = counterpartyKind === 'acquisition target' ? 'targets' : 'competitors';

  const openCompanies = (row: 'subject' | number, trigger: HTMLElement, add = false) => {
    if (editingBusy || actionRef.current) return;
    onEditStart?.(row === 'subject' ? 'subjectCompany' : 'counterparties');
    setCompanyDraft({
      subjectCompany: draft.subjectCompany,
      counterparties: add ? [...draft.counterparties, ''] : [...draft.counterparties],
    });
    setFocusedRow(add ? draft.counterparties.length : row);
    dialogTrigger.current = trigger;
    setCompanyDialog(row === 'subject' ? 'subject' : 'counterparties');
    setDialogOpen(true);
  };
  const applyCompanies = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (editingBusy || actionRef.current || dialogError) return;
    onChange(
      {
        ...draft,
        ...(companyDialog === 'subject'
          ? { subjectCompany: normalizeControlCharacters(companyDraft.subjectCompany) }
          : { counterparties: companyDraft.counterparties.map(normalizeControlCharacters) }),
      },
      companyDialog === 'subject' ? 'subjectCompany' : 'counterparties'
    );
    setDialogOpen(false);
  };
  const cancelCompanies = () => {
    onEditCancel?.(companyDialog === 'subject' ? 'subjectCompany' : 'counterparties');
    setDialogOpen(false);
  };
  const run = async (action: 'refine' | 'confirm') => {
    if (actionRef.current || (action === 'refine' ? refineBlocked : confirmBlocked)) return;
    actionRef.current = true;
    setLocalAction(action);
    try {
      if (action === 'refine') await onRefine(feedback.trim());
      else await onConfirm();
    } finally {
      actionRef.current = false;
      if (mounted.current) setLocalAction(null);
    }
  };
  const renderField = (field: Exclude<AdvisoryReviewField, 'counterpartyKind'>) => {
    if (field === 'subjectCompany')
      return (
        <Button
          type="button"
          disableRipple
          disabled={editingBusy}
          data-testid="advisory-review-subject"
          aria-label={t('advisory:review.editCompanyLabel', {
            company: draft.subjectCompany || t('advisory:review.missingSubject'),
          })}
          onPointerDown={rememberSelectionOnPointerDown}
          onClick={event => {
            if (!isTextSelectionClick(event)) openCompanies('subject', event.currentTarget);
          }}
          data-problem={subjectProblem || undefined}
          sx={[editText, subjectProblem && problemText, focusRing]}
        >
          {draft.subjectCompany || t('advisory:review.missingSubject')}
        </Button>
      );
    if (field === 'counterparties' && !counterpartyKind) return null;
    if (field === 'counterparties')
      return (
        <span>
          {draft.counterparties.map((name, index) => (
            <Fragment key={index}>
              {index === 0
                ? ''
                : index === draft.counterparties.length - 1
                ? ` ${t('advisory:review.listAnd')} `
                : ', '}
              <Button
                type="button"
                disableRipple
                disabled={editingBusy}
                ref={index === 0 ? comparisonTrigger : undefined}
                data-testid={'advisory-review-counterparty-' + index}
                aria-label={t('advisory:review.editCompanyLabel', {
                  company: name || t('advisory:review.missingCompany'),
                })}
                onPointerDown={rememberSelectionOnPointerDown}
                onClick={event => {
                  if (!isTextSelectionClick(event)) openCompanies(index, event.currentTarget);
                }}
                data-problem={companyProblem(name) || undefined}
                sx={[editText, companyProblem(name) && problemText, focusRing]}
              >
                {name || t('advisory:review.missingCompany')}
              </Button>
            </Fragment>
          ))}
          {draft.counterparties.length === 0 && (
            <Button
              type="button"
              disableRipple
              disabled={editingBusy}
              ref={comparisonTrigger}
              data-testid="advisory-review-add-counterparty"
              data-problem="true"
              onPointerDown={rememberSelectionOnPointerDown}
              onClick={event => {
                if (!isTextSelectionClick(event)) openCompanies(0, event.currentTarget, true);
              }}
              sx={[editText, problemText, focusRing]}
            >
              {t('advisory:review.' + (kindKey === 'targets' ? 'addTargets' : 'addCompetitors'))}
            </Button>
          )}
        </span>
      );
    return (
      <AdvisoryPeriodEditor
        value={draft.reportingPeriod}
        problem={periodProblem}
        inline
        busy={editingBusy}
        preserveStagedEditsOnBusy
        onEditStart={() => {
          setPeriodEditing(true);
          onEditStart?.('reportingPeriod');
        }}
        onEditCancel={() => {
          setPeriodEditing(false);
          onEditCancel?.('reportingPeriod');
        }}
        onChange={period => {
          if (!actionRef.current && !editingBusy) {
            onChange({ ...draft, reportingPeriod: period }, 'reportingPeriod');
            setPeriodEditing(false);
          }
        }}
      />
    );
  };

  return (
    <AdvisoryEngagementShell
      position="02"
      total={null}
      eyebrowKey="advisory:review.eyebrow"
      titleKey="advisory:review.title"
      hintKey="advisory:review.hint"
      actions={
        <Button
          variant="contained"
          disableRipple
          disabled={confirmBlocked}
          data-testid="advisory-review-confirm"
          onClick={() => void run('confirm')}
          sx={[pillAction, pressable, focusRing]}
        >
          {t(
            confirming || localAction === 'confirm'
              ? 'advisory:review.confirming'
              : 'advisory:review.confirm'
          )}
        </Button>
      }
    >
      <Box
        data-testid="advisory-review-statement"
        ref={statementElement}
        tabIndex={-1}
        sx={[
          mono,
          theme => ({
            marginTop: theme.visualPrimitives.goalReadOnlyGap,
            fontSize: theme.visualPrimitives.goalReadOnlySize,
            lineHeight: 1.8,
            color: theme.colors.inkPrimary,
          }),
        ]}
      >
        {statement.map((segment, index) => {
          const previous = statement[index - 1];
          const next = statement[index + 1];
          if (segment.type === 'text') {
            // Keep punctuation beside its period without extending the underline.
            const afterPeriod = previous?.type === 'field' && previous.field === 'reportingPeriod';
            return (
              <Fragment key={`text-${index}`}>
                {afterPeriod ? segment.text.replace(/^[,.;:!?]+/, '') : segment.text}
              </Fragment>
            );
          }
          const punctuation =
            segment.field === 'reportingPeriod' && next?.type === 'text'
              ? next.text.match(/^[,.;:!?]+/)?.[0]
              : '';
          // Keep the period editor mounted when a live statement reorders fields or punctuation.
          return segment.field === 'reportingPeriod' ? (
            <Box component="span" key={segment.field} sx={{ whiteSpace: 'nowrap' }}>
              {renderField(segment.field)}
              {punctuation}
            </Box>
          ) : (
            <Fragment key={segment.field}>{renderField(segment.field)}</Fragment>
          );
        })}
      </Box>
      <Box role="status" aria-atomic="true">
        {validPeriod && (periodState === 'defaulted' || periodState === 'failed') ? (
          <AdvisoryReviewWarning testId="advisory-review-period-note">
            {t(
              periodState === 'failed'
                ? 'advisory:review.periodExtractionFailed'
                : 'advisory:period.defaultedHint'
            )}
          </AdvisoryReviewWarning>
        ) : null}
        {warningKeys.map(key => (
          <AdvisoryReviewWarning key={key}>{t('advisory:review.' + key)}</AdvisoryReviewWarning>
        ))}
      </Box>
      {conflicts.map(conflict => (
        <Box
          key={conflict.field}
          data-testid={'advisory-review-conflict-' + conflict.field}
          sx={theme => ({
            marginTop: 3,
            paddingY: 2,
            borderTop: '1px solid ' + theme.visualPrimitives.border,
          })}
        >
          <Typography sx={[eyebrow]}>
            {t('advisory:review.conflictTitle', {
              field: t('advisory:review.field.' + conflict.field),
            })}
          </Typography>
          {conflict.reason ? <Typography sx={[meta]}>{conflict.reason}</Typography> : null}
          {conflict.field === 'counterpartyKind' ? (
            <Typography sx={[meta]}>{t('advisory:review.kindConflictHint')}</Typography>
          ) : null}
          <Typography sx={{ marginTop: 1 }}>
            {t('advisory:review.mine', { value: conflict.mine })}
          </Typography>
          <Typography>{t('advisory:review.suggestion', { value: conflict.suggestion })}</Typography>
          <Box sx={{ display: 'flex', gap: 2, marginTop: 1 }}>
            {conflict.field !== 'counterpartyKind' ? (
              <Button
                disabled={busy || !onResolveConflict}
                data-testid={'advisory-review-keep-' + conflict.field}
                onClick={() => onResolveConflict?.(conflict.field, 'keep')}
                sx={[focusRing]}
              >
                {t('advisory:review.keepMine')}
              </Button>
            ) : null}
            <Button
              disabled={busy || !onResolveConflict}
              data-testid={'advisory-review-suggestion-' + conflict.field}
              onClick={() => onResolveConflict?.(conflict.field, 'suggestion')}
              sx={[focusRing]}
            >
              {t('advisory:review.useSuggestion')}
            </Button>
          </Box>
        </Box>
      ))}
      {!refinementOpen ? (
        <Box sx={theme => ({ marginTop: theme.visualPrimitives.hintGap })}>
          <Button
            ref={refinementTrigger}
            disableRipple
            data-testid="advisory-review-open-refinement"
            aria-expanded={false}
            aria-controls="advisory-review-refinement"
            disabled={editingBusy || !canRefine}
            onClick={() => {
              refinementDismissed.current = false;
              setRefinementOpen(true);
            }}
            sx={[refinementAction, theme => ({ color: theme.colors.inkTertiary }), focusRing]}
          >
            {t('advisory:review.refine')}
          </Button>
          {!canRefine && <Typography sx={[meta]}>{t('advisory:goal.exhaustedBody')}</Typography>}
        </Box>
      ) : (
        <Box
          id="advisory-review-refinement"
          sx={theme => ({ marginTop: theme.visualPrimitives.inputGap })}
        >
          <Typography component="label" htmlFor="advisory-review-feedback" sx={[eyebrow]}>
            {t('advisory:review.feedbackLabel')}
          </Typography>
          <InputBase
            id="advisory-review-feedback"
            multiline
            inputRef={feedbackInput}
            autoFocus
            minRows={3}
            fullWidth
            value={feedback}
            disabled={editingBusy || !canRefine}
            onChange={event => onFeedbackChange(event.target.value)}
            inputProps={{ 'data-testid': 'advisory-review-feedback', maxLength: 8000 }}
            placeholder={t('advisory:review.feedbackPlaceholder')}
            sx={[companyInput]}
          />
          <Box
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, marginTop: 1 }}
          >
            <Button
              disableRipple
              disabled={refineBlocked}
              onClick={() => void run('refine')}
              data-testid="advisory-review-refine"
              sx={[refinementAction, focusRing]}
            >
              {t(
                refining || localAction === 'refine'
                  ? 'advisory:review.refining'
                  : 'advisory:review.refine'
              )}
            </Button>
            <Button
              disableRipple
              disabled={editingBusy}
              onClick={cancelRefinement}
              data-testid="advisory-review-cancel-refinement"
              sx={[refinementAction, focusRing]}
            >
              {t('advisory:review.cancelRefinement')}
            </Button>
            <Typography sx={[meta]}>
              {!canRefine
                ? t('advisory:goal.exhaustedBody')
                : refinementsRemaining !== null
                ? t('advisory:goal.refinementsRemaining', { remaining: refinementsRemaining })
                : ''}
            </Typography>
          </Box>
        </Box>
      )}
      {busy ? (
        <Typography
          role="status"
          data-testid="advisory-review-status"
          sx={[meta, { marginTop: 2 }]}
        >
          {t(
            preparing
              ? 'advisory:review.preparing'
              : saving
              ? 'advisory:review.saving'
              : refining || localAction === 'refine'
              ? 'advisory:review.refining'
              : 'advisory:review.confirming'
          )}
        </Typography>
      ) : null}
      {errorI18nKey ? (
        <Typography role="alert" data-testid="advisory-review-error" sx={[meta, { marginTop: 2 }]}>
          {t(errorI18nKey)}
        </Typography>
      ) : null}
      <Dialog
        open={dialogOpen}
        onClose={cancelCompanies}
        fullWidth
        maxWidth="xs"
        aria-labelledby="advisory-review-company-title"
        disableRestoreFocus
        PaperProps={{ sx: theme => ({ backgroundColor: theme.palette.background.default }) }}
        TransitionProps={{
          onEntered: () => companyInputs.current[String(focusedRow)]?.focus(),
          onExited: () => {
            const trigger = dialogTrigger.current;
            if (trigger?.isConnected) trigger.focus();
            else comparisonTrigger.current?.focus();
          },
        }}
      >
        <Box component="form" onSubmit={applyCompanies}>
          <DialogTitle id="advisory-review-company-title">
            {t(
              companyDialog === 'subject'
                ? 'advisory:review.subjectDialogTitle'
                : counterpartyKind === 'acquisition target'
                ? 'advisory:review.targetsDialogTitle'
                : 'advisory:review.competitorsDialogTitle'
            )}
          </DialogTitle>
          <DialogContent>
            {companyDialog === 'subject' ? (
              <>
                <Typography
                  component="label"
                  htmlFor="advisory-review-company-subject"
                  sx={[eyebrow]}
                >
                  {t('advisory:review.subjectLabel')}
                </Typography>
                <InputBase
                  id="advisory-review-company-subject"
                  fullWidth
                  disabled={editingBusy}
                  value={companyDraft.subjectCompany}
                  inputRef={element => {
                    companyInputs.current.subject = element;
                  }}
                  autoFocus={focusedRow === 'subject'}
                  inputProps={{ 'data-testid': 'advisory-review-company-subject', maxLength: 120 }}
                  sx={[companyInput]}
                  onChange={event =>
                    setCompanyDraft(current => ({ ...current, subjectCompany: event.target.value }))
                  }
                />
              </>
            ) : null}
            {companyDialog === 'counterparties' && counterpartyKind ? (
              <>
                {companyDraft.counterparties.map((name, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InputBase
                      fullWidth
                      disabled={editingBusy}
                      value={name}
                      autoFocus={focusedRow === index}
                      inputRef={element => {
                        companyInputs.current[String(index)] = element;
                      }}
                      inputProps={{
                        'data-testid': 'advisory-review-company-' + index,
                        maxLength: 120,
                        'aria-label': t('advisory:review.companyRowLabel', { index: index + 1 }),
                      }}
                      sx={[companyInput]}
                      onChange={event =>
                        setCompanyDraft(current => ({
                          ...current,
                          counterparties: current.counterparties.map((item, i) =>
                            i === index ? event.target.value : item
                          ),
                        }))
                      }
                    />
                    <Button
                      type="button"
                      disabled={editingBusy}
                      data-testid={'advisory-review-company-remove-' + index}
                      aria-label={t('advisory:review.removeCompanyLabel', {
                        company: name || String(index + 1),
                      })}
                      onClick={() =>
                        setCompanyDraft(current => ({
                          ...current,
                          counterparties: current.counterparties.filter((_, i) => i !== index),
                        }))
                      }
                      sx={[focusRing]}
                    >
                      {t('advisory:counterparties.removeAction')}
                    </Button>
                  </Box>
                ))}
                <Button
                  type="button"
                  disabled={editingBusy}
                  data-testid="advisory-review-company-add"
                  sx={[focusRing, { marginTop: 1 }]}
                  onClick={() => {
                    setFocusedRow(companyDraft.counterparties.length);
                    setCompanyDraft(current => ({
                      ...current,
                      counterparties: [...current.counterparties, ''],
                    }));
                  }}
                >
                  {t('advisory:review.addCompany')}
                </Button>
              </>
            ) : null}
            {dialogError ? (
              <Typography role="status" sx={[meta, { marginTop: 2 }]}>
                {t('advisory:review.' + dialogError)}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={cancelCompanies} sx={[focusRing]}>
              {t('advisory:period.cancelYearAction')}
            </Button>
            <Button
              type="submit"
              disabled={editingBusy || Boolean(dialogError)}
              data-testid="advisory-review-company-apply"
              sx={[focusRing]}
            >
              {t('advisory:review.apply')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </AdvisoryEngagementShell>
  );
}
